import type { NonEmptyReadonlyArray } from "effect/Array";
import type { ConflictCode, QueueReply, ReviewReply, ReviewRequest, SetStatus } from "../core/api.ts";
import type { Kind, SetId } from "../core/model/types.ts";
import type { SessionCard } from "../core/view/session-card.ts";
import { NonNegativeInt, type Rating } from "../srs/types.ts";
import { describeError } from "./errors.ts";
import type { Result, SaveView } from "./flashcard.ts";
import { untilText } from "./picker.ts";

// One attempt's save. The request is built once and resent unchanged on Retry.
export type Save =
  | { readonly tag: "saving"; readonly request: ReviewRequest }
  | { readonly tag: "failed"; readonly request: ReviewRequest; readonly error: unknown }
  | { readonly tag: "confirmed"; readonly request: ReviewRequest; readonly reply: ReviewReply }
  | { readonly tag: "conflicted"; readonly request: ReviewRequest; readonly code: ConflictCode; readonly error: unknown };

export type Turn =
  | { readonly tag: "asking"; readonly card: SessionCard }
  | {
      readonly tag: "answered";
      readonly card: SessionCard;
      readonly given: string;
      readonly provisional: boolean;
      readonly save: Save;
    };

// A stepped card returns after its learning or relearning delay.
export interface Waiting {
  readonly card: SessionCard;
  readonly at: number;
}

export type Entry =
  | { readonly tag: "rated"; readonly card: SessionCard; readonly given: string; readonly rating: Rating }
  | { readonly tag: "dropped"; readonly card: SessionCard; readonly given: string; readonly code: ConflictCode };

export interface Session {
  readonly setId: SetId;
  readonly setName: string;
  readonly turn: Turn | null;
  readonly queue: readonly SessionCard[];
  readonly waiting: readonly Waiting[];
  readonly log: readonly Entry[];
  readonly status: SetStatus;
  readonly ended: boolean;
  readonly reloading: boolean;
}

export type Phase = "asking" | "answered" | "waiting" | "over";

export type Segment = "ok" | "no" | "skip" | "now";

export interface Tally {
  readonly done: number;
  readonly correct: number;
  readonly coming: readonly Waiting[];
}

export interface Coming {
  readonly prompt: string;
  readonly kind: string;
  readonly when: string;
}

export interface Missed {
  readonly prompt: string;
  readonly kind: string;
  readonly answer: string;
  readonly given: string;
}

// Display text for the summary view and standalone stories.
export interface Summary {
  readonly setName: string;
  readonly saved: boolean;
  readonly next: string | null;
  readonly coming: readonly Coming[];
  readonly missed: readonly Missed[];
  readonly right: readonly string[];
  readonly dropped: number;
}

export const KIND_TEXT: Record<Kind, string> = { meaning: "meaning", pinyin: "reading", recall: "recall" };

export function startSession(setId: SetId, cards: NonEmptyReadonlyArray<SessionCard>, status: SetStatus): Session {
  const [first, ...rest] = cards;
  return {
    setId,
    setName: first.set,
    turn: { tag: "asking", card: first },
    queue: rest,
    waiting: [],
    log: [],
    status,
    ended: false,
    reloading: false,
  };
}

export function currentCard(s: Session): SessionCard | undefined {
  return s.ended ? undefined : s.turn?.card;
}

export function phase(s: Session): Phase {
  if (s.ended) return "over";
  if (s.turn) return s.turn.tag;
  return s.waiting.length ? "waiting" : "over";
}

const resolved = (save: Save): boolean => save.tag === "confirmed" || save.tag === "conflicted";

// Next, End session, Back to sets and Continue wait while a save is unresolved or the queue is reloading.
export function locked(s: Session): boolean {
  return s.reloading || (s.turn?.tag === "answered" && !resolved(s.turn.save));
}

// The unresolved attempt with this review id, if it is the current one.
function pending(s: Session, reviewId: string) {
  const t = s.turn;
  return t?.tag === "answered" && !resolved(t.save) && t.save.request.reviewId === reviewId ? t : null;
}

export function answer(s: Session, given: string, provisional: boolean, request: ReviewRequest): Session {
  const t = s.turn;
  if (s.ended || t?.tag !== "asking" || request.reps !== t.card.reps) return s;
  return { ...s, turn: { tag: "answered", card: t.card, given, provisional, save: { tag: "saving", request } } };
}

function insert(waiting: readonly Waiting[], w: Waiting): Waiting[] {
  return [...waiting.filter((x) => x.card.id !== w.card.id), w].sort((a, b) => a.at - b.at);
}

// Apply an unresolved attempt once; newer stepped states return at their due time.
// TODO: Validate cardId and reps = request.reps + 1; a matching reviewId alone can log/requeue the wrong state.
export function saved(s: Session, reply: ReviewReply): Session {
  const t = pending(s, reply.result.reviewId);
  if (!t) return s;
  const { result } = reply;
  const newer = result.card.memory.reps > t.card.reps;
  const card: SessionCard = newer ? { ...t.card, reps: NonNegativeInt.make(result.card.memory.reps), state: result.card.tag } : t.card;
  const steps = newer && (result.card.tag === "learning" || result.card.tag === "relearning");
  return {
    ...s,
    turn: { ...t, save: { tag: "confirmed", request: t.save.request, reply } },
    log: [...s.log, { tag: "rated", card: t.card, given: t.given, rating: result.rating }],
    waiting: steps ? insert(s.waiting, { card, at: result.card.dueAt }) : s.waiting,
    status: reply.set,
  };
}

export function failed(s: Session, reviewId: string, error: unknown): Session {
  const t = pending(s, reviewId);
  if (!t) return s;
  return { ...s, turn: { ...t, save: { tag: "failed", request: t.save.request, error } } };
}

// Retry resends the identical request.
export function retrying(s: Session): Session {
  const t = s.turn;
  if (t?.tag !== "answered" || t.save.tag !== "failed") return s;
  return { ...s, turn: { ...t, save: { tag: "saving", request: t.save.request } } };
}

// A 409: the attempt is discarded and the card dropped from the session. stale_content also reloads the queue.
export function conflicted(s: Session, reviewId: string, code: ConflictCode, error: unknown): Session {
  const t = pending(s, reviewId);
  if (!t) return s;
  return {
    ...s,
    turn: { ...t, save: { tag: "conflicted", request: t.save.request, code, error } },
    log: [...s.log, { tag: "dropped", card: t.card, given: t.given, code }],
    reloading: s.reloading || code === "stale_content",
  };
}

const isBack = (now: number) => (w: Waiting) => w.at <= now;

function take(s: Session, now: number): Session {
  const back = s.waiting.find(isBack(now));
  if (back) return { ...s, turn: { tag: "asking", card: back.card }, waiting: s.waiting.filter((w) => w !== back) };
  const [head, ...rest] = s.queue;
  if (head) return { ...s, turn: { tag: "asking", card: head }, queue: rest };
  return { ...s, turn: null };
}

// Whether advancing has a card to show without fetching the next queue.
export function hasNext(s: Session, now: number): boolean {
  return s.queue.length > 0 || s.waiting.some(isBack(now));
}

export function canAdvance(s: Session): boolean {
  return !s.ended && !locked(s) && s.turn?.tag === "answered";
}

// Due returning cards precede the queue; the controller fetches more when neither is available.
export function advance(s: Session, now: number): Session {
  return canAdvance(s) ? take(s, now) : s;
}

// Continue from the waiting screen, once a card is back.
export function canResume(s: Session, now: number): boolean {
  return !s.ended && s.turn === null && s.waiting.some(isBack(now));
}

export function resume(s: Session, now: number): Session {
  return canResume(s, now) ? take(s, now) : s;
}

// Merge unseen queued cards, removing served cards from waiting; reloads replace the queue.
// TODO: Refresh or discard waiting copies on reload; unserved cards retain stale content/settings revisions.
export function extend(s: Session, reply: QueueReply, now: number, replace = false): Session {
  const queue = replace ? [] : s.queue;
  const held = new Set([...queue.map((c) => c.id), ...(s.turn?.tag === "asking" ? [s.turn.card.id] : [])]);
  const fresh = reply.cards.filter((c) => !held.has(c.id));
  const served = new Set(fresh.map((c) => c.id));
  const next: Session = {
    ...s,
    queue: [...queue, ...fresh],
    waiting: s.waiting.filter((w) => !served.has(w.card.id)),
    status: reply.set,
    reloading: false,
  };
  if (replace || s.ended) return next;
  return s.turn === null || canAdvance(s) ? take(next, now) : next;
}

export function endEarly(s: Session): Session {
  return locked(s) || s.ended ? s : { ...s, ended: true };
}

export function shownResult(s: Session): Result | null {
  const t = s.turn;
  if (t?.tag !== "answered") return null;
  const correct = t.save.tag === "confirmed" ? t.save.reply.result.rating === "good" : t.provisional;
  return { correct, given: t.given };
}

const CONFLICT_NOTE: Record<ConflictCode, string> = {
  stale_card: "Answered elsewhere, so this answer was not saved.",
  stale_content: "This card changed since it was shown, so this answer was not saved; the remaining cards were reloaded.",
  not_available: "This card is not available yet, so this answer was not saved.",
  review_id_reused: "The server refused this save as a reused review id, which is a bug; the answer was not saved.",
};

export function saveView(s: Session): SaveView | null {
  const t = s.turn;
  if (t?.tag !== "answered") return null;
  const save = t.save;
  switch (save.tag) {
    case "saving":
      return { tag: "saving" };
    case "failed":
      return { tag: "failed", ...describeError(save.error) };
    case "conflicted":
      return { tag: "conflicted", note: CONFLICT_NOTE[save.code] };
    case "confirmed":
      return { tag: "confirmed", regraded: (save.reply.result.rating === "good") !== t.provisional };
  }
}

export function segments(s: Session): Segment[] {
  const out: Segment[] = s.log.map((e) => (e.tag === "dropped" ? "skip" : e.rating === "good" ? "ok" : "no"));
  const t = s.turn;
  if (!s.ended && t?.tag === "asking") out.push("now");
  if (t?.tag === "answered" && !resolved(t.save)) out.push(t.provisional ? "ok" : "no");
  return out;
}

const glyph = (card: SessionCard) => card.word ?? card.prompt;

const isRated = (e: Entry): e is Entry & { tag: "rated" } => e.tag === "rated";

export function tally(s: Session): Tally {
  const rated = s.log.filter(isRated);
  return { done: rated.length, correct: rated.filter((e) => e.rating === "good").length, coming: s.waiting };
}

// Uses each card's first confirmed rating.
export function summary(s: Session, now: number): Summary {
  const rated = s.log.filter(isRated);
  const first = new Map<number, Entry & { tag: "rated" }>();
  for (const e of rated) if (!first.has(e.card.id)) first.set(e.card.id, e);
  const firsts = [...first.values()];
  return {
    setName: s.setName,
    saved: first.size > 0,
    next: nextReviewText(s.status, now),
    coming: s.waiting.map((w) => ({ prompt: glyph(w.card), kind: KIND_TEXT[w.card.kind], when: backText(w.at, now) })),
    missed: firsts
      .filter((e) => e.rating === "again")
      .map((e) => ({ prompt: glyph(e.card), kind: KIND_TEXT[e.card.kind], answer: e.card.answer, given: e.given })),
    right: [...new Set(firsts.filter((e) => e.rating === "good").map((e) => glyph(e.card)))],
    dropped: s.log.length - rated.length,
  };
}

export function backText(at: number, now: number): string {
  return at <= now ? "back now" : `back ${untilText(at - now)}`;
}

// Null when reviews are already due.
export function nextReviewText(status: SetStatus, now: number): string | null {
  if (status.nextDueAt === null) return "Nothing scheduled";
  return status.nextDueAt <= now ? null : `Next review ${untilText(status.nextDueAt - now)}`;
}
